import type { Order, OrderKind } from '../../api/types';
import { enviar } from './dataLayer';
import { itemDoPedido, valorDosItens, type ItemGA4 } from './items';

/*
 * Rastreamento do funil — a única API que os componentes usam.
 *
 * Uma função por evento, com os parâmetros tipados. Os nomes seguem os eventos
 * recomendados do GA4 onde eles descrevem o que acontece de verdade, e são
 * próprios onde a loja não tem equivalente (o login pela Steam, a venda de
 * inventário, o preço que muda no fechamento). O plano completo, com o motivo
 * de cada escolha, está no TRACKING.md.
 *
 * Regra que vale para todo este arquivo: nenhum dado pessoal. Nada de SteamID,
 * apelido, trade link, WhatsApp, e-mail ou texto digitado no atendimento. O
 * código do pedido vai porque não identifica quem comprou.
 */

export { definirSessao } from './dataLayer';
export {
  definirCotacao,
  itemDaSacola,
  itemDoAnuncio,
  itemDoGrupo,
  itemDoPedido,
  itemDoTemplate,
  itemPorId,
  reais,
  valorDosItens,
} from './items';
export type { ItemGA4 } from './items';

/** O caminho de compra, para comparar os fluxos no mesmo relatório. */
export type Fluxo = 'cart' | 'kit' | 'upgrade' | 'sell';

export function fluxoDoPedido(kind: OrderKind): Fluxo {
  return kind === 'upgrade' ? 'upgrade' : kind === 'venda' ? 'sell' : 'cart';
}

const MOEDA = 'BRL';

/** Evento de comércio: currency só vai junto de um value, como o GA4 exige. */
function comercio(event: string, dados: { items?: ItemGA4[]; value?: number } & Record<string, unknown>): void {
  const { items, value, ...resto } = dados;
  enviar({
    event,
    ecommerce: {
      ...resto,
      ...(value !== undefined ? { currency: MOEDA, value } : {}),
      ...(items ? { items } : {}),
    },
  });
}

// ─── Navegação ───────────────────────────────────────────────────────────────

/**
 * Visualização de página. O site troca de página sem recarregar, então quem
 * dispara é a ponte do App a cada mudança de rota — e a tag do GA4 no GTM fica
 * com o page view automático desligado, para não contar duas vezes.
 *
 * page_location vai com a query inteira: é nela que chegam UTM e gclid da
 * campanha. As rotas do site não carregam dado pessoal na URL.
 */
let ultimaPagina = '';

export function trackPageView(): void {
  // O mesmo endereço duas vezes seguidas não é outra visita: acontece quando a
  // URL é reescrita sem mudar (replace nos filtros) e, em desenvolvimento, com
  // o StrictMode do React montando a ponte duas vezes.
  if (window.location.href === ultimaPagina) return;
  ultimaPagina = window.location.href;
  enviar({
    event: 'page_view',
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
  });
}

// ─── Login com a Steam ───────────────────────────────────────────────────────

const MARCADOR_LOGIN = 'dms:tracking:login';

export function trackLoginWallView(): void {
  enviar({ event: 'login_wall_view', page_path: window.location.pathname });
}

/**
 * Clique em "Entrar com a Steam". Deixa um marcador na aba: o login sai do
 * site e volta, e é esse marcador que diz, na volta, que a sessão é nova.
 */
export function trackLoginStart(): void {
  try {
    sessionStorage.setItem(MARCADOR_LOGIN, '1');
  } catch {
    // aba anônima ou armazenamento bloqueado: perde-se o evento de login, só
  }
  enviar({ event: 'login_start', page_path: window.location.pathname });
}

/** Chamado pela ponte do App quando a sessão carrega. Só dispara uma vez. */
export function trackLoginSeVoltouDaSteam(logado: boolean): void {
  try {
    if (sessionStorage.getItem(MARCADOR_LOGIN) !== '1') return;
    sessionStorage.removeItem(MARCADOR_LOGIN);
  } catch {
    return;
  }
  if (logado) enviar({ event: 'login', method: 'steam' });
}

/** O login voltou com erro. Leva o tipo e, quando houver, o código técnico. */
export function trackLoginError(tipo: string, codigo?: string | null): void {
  try {
    sessionStorage.removeItem(MARCADOR_LOGIN);
  } catch {
    // sem armazenamento, nada a limpar
  }
  enviar({ event: 'login_error', error_type: tipo, ...(codigo ? { error_code: codigo } : {}) });
}

export function trackSignUp(): void {
  enviar({ event: 'sign_up', method: 'steam' });
}

// ─── Descoberta e produto ────────────────────────────────────────────────────

/**
 * Última lista enviada por lista. A mesma lista refaz a busca ao voltar para a
 * página ou quando o cache responde de novo; sem isto cada volta contaria uma
 * visualização a mais.
 */
const ultimaLista = new Map<string, string>();

export function trackViewItemList(lista: { id: string; nome: string }, items: ItemGA4[]): void {
  if (items.length === 0) return;
  // O GA4 aceita até 200 itens, mas a lista só precisa do que aparece primeiro.
  const visiveis = items.slice(0, 20);
  const assinatura = visiveis.map((i) => i.item_id).join('|');
  if (ultimaLista.get(lista.id) === assinatura) return;
  ultimaLista.set(lista.id, assinatura);
  comercio('view_item_list', { item_list_id: lista.id, item_list_name: lista.nome, items: visiveis });
}

export function trackSelectItem(lista: { id: string; nome: string }, item: ItemGA4): void {
  comercio('select_item', { item_list_id: lista.id, item_list_name: lista.nome, items: [item] });
}

let temporizadorBusca: number | undefined;
let ultimaBusca = '';

/**
 * Busca pelo nome. Espera a digitação parar (o campo avisa a cada tecla) e
 * ignora termos curtos e repetidos. O termo é nome de skin, não dado pessoal.
 */
export function trackSearch(termo: string): void {
  try {
    window.clearTimeout(temporizadorBusca);
    const limpo = termo.trim().toLowerCase();
    if (limpo.length < 2 || limpo === ultimaBusca) return;
    temporizadorBusca = window.setTimeout(() => {
      ultimaBusca = limpo;
      enviar({ event: 'search', search_term: limpo.slice(0, 100) });
    }, 1200);
  } catch {
    // rastreamento nunca interrompe o site
  }
}

export function trackAddToWishlist(item: ItemGA4): void {
  comercio('add_to_wishlist', { items: [item], value: item.price });
}

export function trackViewItem(item: ItemGA4): void {
  comercio('view_item', { items: [item], value: item.price });
}

// ─── Sacola e pedido ─────────────────────────────────────────────────────────

export function trackAddToCart(item: ItemGA4): void {
  comercio('add_to_cart', { items: [item], value: item.price });
}

export function trackRemoveFromCart(item: ItemGA4): void {
  comercio('remove_from_cart', { items: [item], value: item.price });
}

export function trackViewCart(items: ItemGA4[]): void {
  if (items.length === 0) return;
  comercio('view_cart', { items, value: valorDosItens(items) });
}

export function trackBeginCheckout(fluxo: Fluxo, items: ItemGA4[], valorBrl?: number): void {
  comercio('begin_checkout', { flow: fluxo, items, value: valorBrl ?? valorDosItens(items) });
}

/** O fechamento devolveu outro total ou menos peças, e o site avisou. */
export function trackCheckoutPriceChange(dados: { pecasFora: number; antesBrl: number; depoisBrl: number }): void {
  enviar({
    event: 'checkout_price_change',
    items_removed: dados.pecasFora,
    value_before: dados.antesBrl,
    value_after: dados.depoisBrl,
    currency: MOEDA,
  });
}

/** O valor que o pedido representa para a loja, conforme o tipo. */
function valorDoPedido(o: Order): number {
  return o.kind === 'upgrade' ? o.toPayBrl : o.kind === 'venda' ? o.creditBrl : o.totalBrl;
}

function itensQueSaem(o: Order): ItemGA4[] {
  return o.items.filter((i) => i.side === 'saida').map(itemDoPedido);
}

/**
 * Pedido criado. Na loja, a "entrega" é o trade link da Steam, que o pedido
 * congela no momento em que nasce — por isso add_shipping_info.
 */
export function trackAddShippingInfo(pedido: Order): void {
  comercio('add_shipping_info', {
    flow: fluxoDoPedido(pedido.kind),
    shipping_tier: 'steam_trade',
    transaction_id: pedido.code,
    items: itensQueSaem(pedido),
    value: valorDoPedido(pedido),
  });
}

export function trackOrderCancel(kind: OrderKind): void {
  enviar({ event: 'order_cancel', flow: fluxoDoPedido(kind) });
}

/**
 * Envio do pedido ao atendimento — a conversão principal.
 *
 * Pedido de venda de inventário não entra aqui: nele é a loja que compra, e
 * contar isso como lead de compra misturaria as duas coisas no Google Ads.
 */
export function trackPedidoEnviado(pedido: Order): void {
  if (pedido.kind === 'venda') {
    enviar({
      event: 'sell_lead',
      transaction_id: pedido.code,
      currency: MOEDA,
      value: pedido.creditBrl,
    });
    return;
  }
  comercio('generate_lead', {
    flow: fluxoDoPedido(pedido.kind),
    transaction_id: pedido.code,
    items: itensQueSaem(pedido),
    value: valorDoPedido(pedido),
  });
}

// ─── Kits ────────────────────────────────────────────────────────────────────

export function trackKitBuild(dados: { items: ItemGA4[]; valorBrl?: number; descontoPct: number }): void {
  comercio('kit_build', { flow: 'kit', discount_pct: dados.descontoPct, items: dados.items, value: dados.valorBrl });
}

// ─── Upgrade e venda de inventário ───────────────────────────────────────────

export function trackUpgradeQuote(dados: { aPagarBrl: number; creditoBrl: number }): void {
  enviar({
    event: 'upgrade_quote',
    flow: 'upgrade',
    currency: MOEDA,
    value: dados.aPagarBrl,
    trade_in_value: dados.creditoBrl,
  });
}

export function trackSellQuote(dados: { creditoBrl: number; pecas: number }): void {
  enviar({ event: 'sell_quote', flow: 'sell', currency: MOEDA, value: dados.creditoBrl, items_count: dados.pecas });
}

export function trackSellOrderCreated(pedido: Order): void {
  enviar({
    event: 'sell_order_created',
    flow: 'sell',
    transaction_id: pedido.code,
    currency: MOEDA,
    value: pedido.creditBrl,
    items_count: pedido.items.filter((i) => i.side === 'entrada').length,
  });
}

// ─── Apoio ───────────────────────────────────────────────────────────────────

export function trackSupportOpen(): void {
  enviar({ event: 'support_open', page_path: window.location.pathname });
}
